/**
 * Shared spreadsheet-parsing primitives for the bulk importers.
 *
 * The Assets page grew a careful little parser — header-row detection, cross-locale header
 * matching, whitespace/curly-quote folding — that the Locations importer badly needed too. Rather
 * than copy it, the reusable parts live here; each page still owns its own column dictionary and
 * value coercion, which is where the two genuinely differ.
 */

import * as XLSX from 'xlsx';

/**
 * Folds a header cell to a stable matching key: non-breaking spaces, curly apostrophes, repeated
 * whitespace and case all disappear — so "Nom ", "Nom", "Type d’actif" and "Type d'actif"
 * all resolve to the same thing.
 *
 * @param {*} header raw cell value
 * @returns {string} normalised key
 */
export const normalizeHeader = (header) => String(header ?? '')
    .replace(/ /g, ' ')
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

/**
 * Trims and collapses internal whitespace. Names go through this before they are sent: the
 * location name column has a NO PAD collation, so a stray trailing space from a spreadsheet makes
 * every later name lookup silently miss.
 *
 * @param {*} value raw cell value
 * @returns {string} cleaned text
 */
export const normalizeName = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

/**
 * Builds "any locale's column header → canonical mapping key".
 *
 * A file exported from a French session must import in an English one and vice versa, so every
 * loaded language's label for a column is registered, not just the active one.
 *
 * @param {object} params
 * @param {object} params.mapping column dictionary keyed by canonical English header
 * @param {object} params.i18n the i18next instance
 * @param {function} params.labelLookup (bundle, i18nKey) => translated header label, or undefined
 * @returns {object} normalised header → mapping key
 */
export const buildCrossLocaleHeaderMap = ({ mapping, i18n, labelLookup }) => {
    const headerMap = {};
    const loadedLangs = Object.keys(i18n?.store?.data || {});

    Object.keys(mapping).forEach((mapKey) => {
        headerMap[normalizeHeader(mapKey)] = mapKey;
        (mapping[mapKey].aliases || []).forEach((alias) => {
            headerMap[normalizeHeader(alias)] = mapKey;
        });
        const i18nKey = mapping[mapKey].template?.headerI18nKey;
        if (i18nKey && labelLookup) {
            loadedLangs.forEach((lang) => {
                const bundle = i18n.getResourceBundle(lang, 'translation');
                const label = labelLookup(bundle, i18nKey);
                if (label) headerMap[normalizeHeader(label)] = mapKey;
            });
        }
    });
    return headerMap;
};

/**
 * Reads a File into a worksheet plus its rows as raw arrays.
 *
 * @param {File} file the uploaded spreadsheet
 * @returns {Promise<{worksheet: object, rawRows: Array, originRow: number}>}
 */
export const readWorkbook = async (file) => {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { raw: true });
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    const range = worksheet['!ref'] ? XLSX.utils.decode_range(worksheet['!ref']) : null;
    return {
        worksheet,
        originRow: range ? range.s.r : 0,
        rawRows: XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false }),
    };
};

/**
 * Finds the row that actually holds the column headers.
 *
 * The template ships instruction rows above the headers, but people delete them, or paste their
 * data into a sheet of their own with a title row on top. Assuming a fixed offset breaks on both,
 * so the first rows are scanned and scored instead.
 *
 * @param {object} params
 * @param {Array} params.rawRows sheet rows as arrays
 * @param {object} params.headerMap from {@link buildCrossLocaleHeaderMap}
 * @param {string} params.requiredKey mapping key that must be present (the name column)
 * @param {number} [params.scanRows] how many rows to consider
 * @returns {number} 0-based offset of the header row, or -1 when none looks like one
 */
export const detectHeaderRow = ({ rawRows, headerMap, requiredKey, scanRows = 15 }) => {
    let headerOffset = -1;
    let bestScore = 0;
    const limit = Math.min(rawRows.length, scanRows);

    for (let i = 0; i < limit; i += 1) {
        const cells = rawRows[i] || [];
        let score = 0;
        let hasRequired = false;
        cells.forEach((cell) => {
            const key = headerMap[normalizeHeader(cell)];
            if (key) {
                score += 1;
                if (key === requiredKey) hasRequired = true;
            }
        });
        // The required column plus at least one more, so a data row that happens to match a single
        // header cannot be mistaken for the header row.
        if (hasRequired && score >= 2 && score > bestScore) {
            bestScore = score;
            headerOffset = i;
        }
    }
    return headerOffset;
};

/**
 * Reads the sheet as objects keyed by the detected header row.
 *
 * @param {object} worksheet the worksheet
 * @param {number} originRow first row of the used range
 * @param {number} headerOffset offset returned by {@link detectHeaderRow}
 * @returns {Array<object>} data rows
 */
export const readRowsFromHeader = (worksheet, originRow, headerOffset) =>
    XLSX.utils.sheet_to_json(worksheet, { raw: false, range: originRow + headerOffset });

/**
 * Parses a day-first date cell into the ISO form the API expects.
 *
 * Rule 7: the product never uses month-first order, so 03/04/2026 is unambiguously 3 April.
 * Returns the input untouched when it does not look like a date, letting the server report it as a
 * bad cell on the right row rather than guessing here.
 *
 * @param {*} value raw cell value
 * @returns {string|null} yyyy-MM-dd, or the original text
 */
export const toIsoDate = (value) => {
    const text = String(value ?? '').trim();
    if (!text) return null;

    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

    const dayFirst = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})/.exec(text);
    if (dayFirst) {
        const day = dayFirst[1].padStart(2, '0');
        const month = dayFirst[2].padStart(2, '0');
        return `${dayFirst[3]}-${month}-${day}`;
    }
    return text;
};

/**
 * Row numbers as the person editing the file sees them in Excel: 1-based, counting the header row
 * and anything above it. Issues have to point at the line they will actually look at.
 *
 * @param {number} index 0-based index within the data rows
 * @param {number} originRow first row of the used range
 * @param {number} headerOffset offset of the header row
 * @returns {number} spreadsheet row number
 */
export const spreadsheetRowNumber = (index, originRow, headerOffset) =>
    originRow + headerOffset + index + 2;
